<script setup>
import { ref, computed, provide } from 'vue';
import PzToast from './PzToast.vue';

const props = defineProps({
  position: {
    type: String,
    default: 'top-right',
    validator: (value) => ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'].includes(value)
  },
  maxToasts: {
    type: Number,
    default: 5
  }
});

const toasts = ref([]);
let nextId = 0;

const containerClasses = computed(() => {
  const classes = ['pz-toast-container'];
  classes.push(`pz-toast-container-${props.position}`);
  return classes.join(' ');
});

const addToast = (options) => {
  const id = nextId++;
  const toast = {
    id,
    ...options
  };
  
  // Remove oldest toast if we exceed max
  if (toasts.value.length >= props.maxToasts) {
    toasts.value.shift();
  }
  
  toasts.value.push(toast);
  return id;
};

const removeToast = (id) => {
  const index = toasts.value.findIndex(t => t.id === id);
  if (index > -1) {
    toasts.value.splice(index, 1);
  }
};

// Provide methods for child components
provide('addToast', addToast);
provide('removeToast', removeToast);

// Expose methods for external use
defineExpose({
  addToast,
  removeToast,
  toasts
});
</script>

<template>
  <Teleport to="body">
    <div :class="containerClasses">
      <TransitionGroup name="toast">
        <PzToast
          v-for="toast in toasts"
          :key="toast.id"
          :variant="toast.variant"
          :title="toast.title"
          :message="toast.message"
          :duration="toast.duration"
          :closable="toast.closable"
          :icon="toast.icon"
          :showProgress="toast.showProgress"
          @close="removeToast(toast.id)"
          @click="toast.onClick && toast.onClick()"
        />
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
/* Import the toast styles */
@import '@/assets/css/pz-ui-components.css';

/* Fallback inline styles for container */
.pz-toast-container {
  position: fixed;
  z-index: 50;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  max-width: 420px;
}

.pz-toast-container-top-right {
  top: 0;
  right: 0;
}

.pz-toast-container-top-left {
  top: 0;
  left: 0;
}

.pz-toast-container-top-center {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

.pz-toast-container-bottom-right {
  bottom: 0;
  right: 0;
}

.pz-toast-container-bottom-left {
  bottom: 0;
  left: 0;
}

.pz-toast-container-bottom-center {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

/* Toast animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease-in-out;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%) scale(0.75);
  opacity: 0;
}

/* Left position animations */
.pz-toast-container-top-left .toast-enter-from,
.pz-toast-container-bottom-left .toast-enter-from,
.pz-toast-container-top-left .toast-leave-to,
.pz-toast-container-bottom-left .toast-leave-to {
  transform: translateX(-100%);
}

/* Center position animations */
.pz-toast-container-top-center .toast-enter-from {
  transform: translateX(0) translateY(-100%);
}

.pz-toast-container-bottom-center .toast-enter-from {
  transform: translateX(0) translateY(100%);
}

.pz-toast-container-top-center .toast-leave-to,
.pz-toast-container-bottom-center .toast-leave-to {
  transform: translateX(0) scale(0.95);
  opacity: 0;
}
</style>